import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  Pagination
} from 'antd'
import {
  DownloadOutlined,
  EyeOutlined,
  FileProtectOutlined,
  IdcardOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined
} from '@ant-design/icons'
import PurchaseConsentModal from '../components/PurchaseConsentModal'
import ResponsiveSelect from '../components/common/ResponsiveSelect'
import styled from 'styled-components'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import api, { getApiErrorMessage } from '../utils/api'
import {
  exportClassTicketsUrl,
  getClassTicketDetail,
  issueClassTicket,
  listClassTickets,
  listClassTicketTemplates,
  redeemClassTicket,
  refundClassTicket,
  voidClassTicket
} from '../api/classTicket'
import { formatTz, now as tzNow } from '../utils/timezone'
import {
  describeValidity,
  formatTemplateExpiry
} from '../utils/classTicketExpiry'

const { Title, Text } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);

  .ant-card-body {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      flex-direction: column;
      align-items: stretch;
      justify-content: center;
      text-align: center;
    }

    .ant-card-body > .ant-space {
      justify-content: center;
      width: 100%;
    }
  }
`

const TableCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);
`

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`

const DesktopTable = styled(Table)`
  @media (max-width: 768px) {
    display: none;
  }
`

const MobileList = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`

const MobileItemCard = styled.div`
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MobileTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`

const MobileTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #2c2531;
`

const MobileMeta = styled.div`
  font-size: 12px;
  color: #8b7d7d;
  margin-top: 2px;
`

const MobileMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: #6b7280;

  span b {
    color: #1d1d1f;
    font-weight: 600;
    margin-left: 2px;
  }
`

const MobileActionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;

  .ant-btn {
    flex: 1;
  }
`

const MobilePagination = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }
`

const STATUS_META = {
  active: { text: '可使用', color: 'green' },
  used_up: { text: '使用完畢', color: 'default' },
  expired: { text: '已過期', color: 'orange' },
  voided: { text: '已作廢', color: 'red' }
}

const ClassTicketRecordsPage = () => {
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const navigate = useNavigate()
  // 從「發放紀錄」點進來會帶 ?ticketCode=，直接篩出那張票
  const [searchParams] = useSearchParams()
  const initialTicketCode = searchParams.get('ticketCode') || ''
  const businessId = user?.businessId

  const featureEnabled = hasFeature('classTicket')

  // ---------- list state ----------
  const [loading, setLoading] = useState(false)
  const [tickets, setTickets] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = 20
  const [filters, setFilters] = useState({
    keyword: '',
    ticketCode: initialTicketCode,
    templateId: '',
    status: 'all'
  })
  const [filterDraft, setFilterDraft] = useState(filters)

  // ---------- templates / services / employees ----------
  const [templates, setTemplates] = useState([])
  const [services, setServices] = useState([])
  const [employees, setEmployees] = useState([])
  const serviceMap = useMemo(() => {
    const m = new Map()
    services.forEach((s) => m.set(String(s._id), s))
    return m
  }, [services])

  // ---------- detail drawer ----------
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // ---------- redeem/refund/void modal ----------
  const [actionModal, setActionModal] = useState({
    open: false,
    type: null,
    submitting: false
  })
  const [actionForm] = Form.useForm()

  // ---------- issue modal ----------
  const [issueOpen, setIssueOpen] = useState(false)
  const [issueSubmitting, setIssueSubmitting] = useState(false)
  const [issueForm] = Form.useForm()
  const [customerOptions, setCustomerOptions] = useState([])
  const [customerLoading, setCustomerLoading] = useState(false)
  const [feeSettings, setFeeSettings] = useState({ cardFeeRate: null, mobilePaymentFeeRate: null })

  // ---------- 購買同意書 ----------
  const [consent, setConsent] = useState({
    open: false,
    target: 'classTicket',
    customerId: null,
    customerName: '',
    variables: {},
    pendingValues: null
  })

  const fetchTickets = useCallback(
    async (overrides = {}) => {
      if (!businessId) return
      setLoading(true)
      try {
        const params = {
          page,
          pageSize,
          ...filters,
          ...overrides
        }
        const res = await listClassTickets(businessId, params)
        setTickets(res?.data?.data?.items || [])
        setTotal(res?.data?.data?.total || 0)
      } catch (err) {
        message.error(getApiErrorMessage(err, '取得堂票列表失敗'))
      } finally {
        setLoading(false)
      }
    },
    [businessId, page, filters]
  )

  const fetchTemplates = useCallback(async () => {
    if (!businessId) return
    try {
      const res = await listClassTicketTemplates(businessId, {
        includeInactive: '1'
      })
      setTemplates(res?.data?.data || [])
    } catch (err) {
      console.warn('fetch templates failed', err)
    }
  }, [businessId])

  const fetchServices = useCallback(async () => {
    if (!businessId) return
    try {
      const res = await api.get(`/api/business_B/${businessId}/products`)
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : []
      setServices(
        list.map((p) => ({
          _id: p._id,
          name: p.name,
          basePrice: p.basePrice ?? p.price ?? 0
        }))
      )
    } catch (err) {
      console.warn('fetch services failed', err)
    }
  }, [businessId])

  const fetchEmployees = useCallback(async () => {
    if (!businessId) return
    try {
      const res = await api.get(`/api/employees/${businessId}`)
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : []
      setEmployees(list)
    } catch (err) {
      console.warn('fetch employees failed', err)
    }
  }, [businessId])

  const fetchFeeSettings = useCallback(async () => {
    if (!businessId) return
    try {
      const res = await api.get(`/api/business_B/${businessId}/payment-fee-settings`)
      const data = res?.data?.data || res?.data || {}
      setFeeSettings({
        cardFeeRate: data.cardFeeRate ?? null,
        mobilePaymentFeeRate: data.mobilePaymentFeeRate ?? null
      })
    } catch (err) {
      console.warn('fetch payment fee settings failed', err)
    }
  }, [businessId])

  useEffect(() => {
    if (featureEnabled) {
      fetchTemplates()
      fetchServices()
      fetchEmployees()
      fetchFeeSettings()
    }
  }, [featureEnabled, fetchTemplates, fetchServices, fetchEmployees, fetchFeeSettings])

  useEffect(() => {
    if (featureEnabled) fetchTickets()
  }, [fetchTickets, featureEnabled])

  // ---------- handlers ----------
  const applyFilters = () => {
    setFilters(filterDraft)
    setPage(1)
  }
  const resetFilters = () => {
    const blank = {
      keyword: '',
      ticketCode: '',
      templateId: '',
      status: 'all'
    }
    setFilterDraft(blank)
    setFilters(blank)
    setPage(1)
  }

  const openDetail = async (ticket) => {
    setDrawerOpen(true)
    setDetailLoading(true)
    try {
      const res = await getClassTicketDetail(businessId, ticket._id)
      setDetail(res?.data?.data || null)
    } catch (err) {
      message.error(getApiErrorMessage(err, '取得詳情失敗'))
    } finally {
      setDetailLoading(false)
    }
  }

  const reloadDetail = async () => {
    if (!detail?._id) return
    try {
      const res = await getClassTicketDetail(businessId, detail._id)
      setDetail(res?.data?.data || null)
    } catch (err) {
      // ignore
    }
  }

  const openAction = (type) => {
    actionForm.resetFields()
    // 施作人員預設帶入登入者本人（若在員工清單中）
    const self = employees.find(
      (e) => String(e._id) === String(user?.userId || '')
    )
    actionForm.setFieldsValue({
      count: 1,
      serviceId: undefined,
      note: '',
      performerId: self?._id
    })
    setActionModal({ open: true, type, submitting: false })
  }

  const submitAction = async () => {
    try {
      const values = await actionForm.validateFields()
      setActionModal((s) => ({ ...s, submitting: true }))
      if (actionModal.type === 'redeem') {
        const svc = services.find((s) => String(s._id) === values.serviceId)
        await redeemClassTicket(businessId, detail._id, {
          count: values.count,
          serviceId: values.serviceId,
          serviceName: svc?.name || '',
          note: values.note || '',
          performerId: values.performerId || null
        })
        message.success('已扣張')
      } else if (actionModal.type === 'refund') {
        await refundClassTicket(businessId, detail._id, {
          count: values.count,
          note: values.note || ''
        })
        message.success('已退張')
      } else if (actionModal.type === 'void') {
        await voidClassTicket(businessId, detail._id, {
          reason: values.note || ''
        })
        message.success('已作廢')
      }
      setActionModal({ open: false, type: null, submitting: false })
      await reloadDetail()
      fetchTickets()
    } catch (err) {
      if (err?.errorFields) return
      message.error(getApiErrorMessage(err, '操作失敗'))
      setActionModal((s) => ({ ...s, submitting: false }))
    }
  }

  // ---------- issue ----------
  const searchCustomers = useCallback(
    async (keyword) => {
      if (!businessId || !keyword || keyword.trim().length < 1) {
        setCustomerOptions([])
        return
      }
      setCustomerLoading(true)
      try {
        const res = await api.get(
          `/api/business_B/${businessId}/customers/search`,
          { params: { searchTerm: keyword.trim() } }
        )
        const list = res?.data?.customers || res?.data?.data || []
        setCustomerOptions(
          (Array.isArray(list) ? list : []).slice(0, 30).map((c) => ({
            value: c._id,
            label: `${c.name || c.username || '未命名'}${c.phone ? ` (${c.phone})` : ''}`,
            customer: c
          }))
        )
      } catch (err) {
        console.warn('search customers failed', err)
      } finally {
        setCustomerLoading(false)
      }
    },
    [businessId]
  )

  // 實際發放（可帶入已簽的購買同意書 consentSignatureId）
  const doIssue = async (values, consentSignatureId = null) => {
    setIssueSubmitting(true)
    try {
      await issueClassTicket(businessId, {
        templateId: values.templateId,
        customerId: values.customerId,
        salesPersonnel: values.salesPersonnel || '',
        paymentMethod: values.paymentMethod || 'cash',
        note: values.note || '',
        issueType: values.issueType || 'sale',
        ...(consentSignatureId ? { consentSignatureId } : {})
      })
      message.success('已成功發放堂票')
      setConsent((c) => ({ ...c, open: false }))
      setIssueOpen(false)
      issueForm.resetFields()
      fetchTickets()
    } catch (err) {
      // 428＝需先簽署購買同意書
      if (err?.response?.status === 428) {
        message.warning(
          err?.response?.data?.message || '此發放需先完成購買同意書簽署'
        )
      } else {
        message.error(getApiErrorMessage(err, '發放失敗'))
      }
    } finally {
      setIssueSubmitting(false)
    }
  }

  const closeConsent = () => setConsent((c) => ({ ...c, open: false }))

  /**
   * 補簽／補發這張票的購買同意書。
   *
   * 發放當下按了「略過（稍後再簽）」的票，以前在這裡完全沒有入口，
   * 而且那時候連待簽紀錄都不會留 —— 那份同意書等於永遠消失了。
   * 現在從票本身開同一個視窗（reminderMode＝不牽涉發放，只處理同意書），
   * 現場簽或傳給客人都可以；subjectRef 帶票號，簽完就綁在這張票上。
   */
  const openConsentForTicket = (r) => {
    setConsent({
      open: true,
      reminderMode: true,
      target: 'classTicket',
      classTicketTemplateId: r.templateId,
      customerId: r.customerId,
      customerName: r.customerName,
      subjectRef: r._id,
      variables: {
        customerName: r.customerName,
        planName: r.templateName,
        ticketCode: r.ticketCode
      },
      pendingValues: null
    })
  }

  /* 同意書視窗的四個出口：補簽模式只關視窗，發放模式才接著發票 */
  const handleConsentNoForm = () => {
    if (consent.reminderMode) {
      message.info('這種堂票沒有綁定購買同意書')
      closeConsent()
      return
    }
    doIssue(consent.pendingValues, null)
  }
  const handleConsentSkip = () => {
    if (consent.reminderMode) {
      closeConsent()
      return
    }
    doIssue(consent.pendingValues, null)
  }
  const handleConsentSigned = (sigId) => {
    if (consent.reminderMode) {
      message.success('已完成簽署')
      closeConsent()
      fetchTickets()
      return
    }
    doIssue(consent.pendingValues, sigId)
  }
  const handleConsentSentRemote = () => {
    if (consent.reminderMode) {
      closeConsent()
      return
    }
    doIssue(consent.pendingValues, null)
  }

  // 按「發放」：先驗證表單，再開購買同意書 Modal（無綁定則直接發放）
  const submitIssue = async () => {
    try {
      const values = await issueForm.validateFields()
      // 贈送 / 補發：非購買，直接發放，不觸發購買同意書
      if ((values.issueType || 'sale') === 'gift') {
        doIssue(values, null)
        return
      }
      const tpl = templates.find((t) => t._id === values.templateId)
      const custLabel =
        customerOptions.find((o) => o.value === values.customerId)?.label || ''
      const variables = {
        planName: tpl?.name || '',
        // 指定到期日的範本直接用那一天，天數制才從今天起算
        expiryDate: formatTemplateExpiry(tpl) || '',
        price: tpl?.price || 0,
        amount: tpl?.price || 0,
        customerName: String(custLabel).split('（')[0].split(' ')[0]
      }
      setConsent({
        open: true,
        reminderMode: false, // 發放前簽署：簽完（或略過）就接著發放
        subjectRef: null, // 票還沒建立，簽名之後由發放帶入
        target: 'classTicket',
        // 帶上正在發哪一種堂票 —— 同意書若有限定堂票，只有對得上的才會跳出
        classTicketTemplateId: values.templateId,
        customerId: values.customerId,
        customerName: variables.customerName,
        variables,
        pendingValues: values
      })
    } catch (err) {
      if (err?.errorFields) return
      message.error(getApiErrorMessage(err, '發放失敗'))
    }
  }

  // ---------- export ----------
  const handleExport = async (type) => {
    try {
      const res = await api.get(exportClassTicketsUrl(businessId, type), {
        responseType: 'blob'
      })
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `class_ticket_${type}_${tzNow().format('YYYYMMDD_HHmm')}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      message.error(getApiErrorMessage(err, '匯出失敗'))
    }
  }

  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <Spin />
      </PageWrapper>
    )
  }
  if (!featureEnabled) {
    return (
      <PageWrapper>
        <Result
          status='warning'
          title='您的方案尚未開通堂票模組'
          subTitle='請升級至高階方案以使用堂票功能。'
        />
      </PageWrapper>
    )
  }

  // ---------- table columns ----------
  const columns = [
    {
      title: '堂票編號',
      dataIndex: 'ticketCode',
      key: 'ticketCode',
      width: 120,
      render: (v) => <Text code>{v}</Text>
    },
    {
      title: '會員',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (v, r) => (
        <div>
          <div>{v || '—'}</div>
          {r.customerPhone && (
            <Text type='secondary' style={{ fontSize: 12 }}>
              {r.customerPhone}
            </Text>
          )}
        </div>
      )
    },
    {
      title: '堂票名稱',
      dataIndex: 'templateName',
      key: 'templateName'
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v) => {
        const meta = STATUS_META[v] || { text: v, color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
      }
    },
    {
      title: '剩餘 / 總張數',
      key: 'count',
      width: 130,
      align: 'right',
      render: (_, r) => (
        <Text>
          <Text strong>{r.remainingCount}</Text> / {r.totalCount}
        </Text>
      )
    },
    {
      title: '有效日期',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: 160,
      render: (v) => formatTz(v, 'YYYY/MM/DD HH:mm', '—')
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v) => formatTz(v, 'YYYY/MM/DD HH:mm', '—')
    },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      fixed: 'right',
      align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title='詳情'>
            <Button
              type='text'
              size='small'
              icon={<EyeOutlined />}
              onClick={() => openDetail(r)}
            />
          </Tooltip>
          <Tooltip title='購買同意書（補簽／補發）'>
            <Button
              type='text'
              size='small'
              icon={<FileProtectOutlined />}
              disabled={!r.customerId}
              onClick={() => openConsentForTicket(r)}
            />
          </Tooltip>
          <Tooltip title='前往會員'>
            <Button
              type='text'
              size='small'
              icon={<UserOutlined />}
              onClick={() => navigate(`/customers/${r.customerId}`)}
            />
          </Tooltip>
        </Space>
      )
    }
  ]

  return (
    <PageWrapper>
      <ContentWrapper>
        <HeaderCard>
          <Space size='middle' align='center'>
            <IdcardOutlined style={{ fontSize: 22, color: '#d5728a' }} />
            <Title level={4} style={{ margin: 0 }}>
              堂票紀錄列表
            </Title>
          </Space>
          <Space wrap>
            {/* 匯出按鈕暫時隱藏 — 之後若要恢復取消註解即可
            <Button
              icon={<DownloadOutlined />}
              onClick={() => handleExport('usages')}
            >
              匯出使用紀錄
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => handleExport('tickets')}
            >
              匯出新增紀錄
            </Button>
            */}
            <Button icon={<ReloadOutlined />} onClick={() => fetchTickets()}>
              重新整理
            </Button>
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => {
                issueForm.resetFields()
                setCustomerOptions([])
                // 銷售人員預設帶入登入者本人（若在員工清單中）
                const self = employees.find(
                  (e) => String(e._id) === String(user?.userId || '')
                )
                if (self) {
                  issueForm.setFieldsValue({
                    salesPersonnel: self.name || self.username || ''
                  })
                }
                setIssueOpen(true)
              }}
            >
              發放堂票
            </Button>
          </Space>
        </HeaderCard>

        <TableCard>
          <FilterBar>
            <Input
              allowClear
              placeholder='會員姓名 / 手機'
              prefix={<SearchOutlined />}
              style={{ width: 220 }}
              value={filterDraft.keyword}
              onChange={(e) =>
                setFilterDraft((d) => ({ ...d, keyword: e.target.value }))
              }
              onPressEnter={applyFilters}
            />
            <Input
              allowClear
              placeholder='堂票編號'
              style={{ width: 160 }}
              value={filterDraft.ticketCode}
              onChange={(e) =>
                setFilterDraft((d) => ({ ...d, ticketCode: e.target.value }))
              }
              onPressEnter={applyFilters}
            />
            <Select
              allowClear
              placeholder='堂票名稱'
              style={{ width: 220 }}
              showSearch
              optionFilterProp='label'
              value={filterDraft.templateId || undefined}
              options={templates.map((t) => ({ label: t.name, value: t._id }))}
              onChange={(v) =>
                setFilterDraft((d) => ({ ...d, templateId: v || '' }))
              }
            />
            <Select
              style={{ width: 130 }}
              value={filterDraft.status}
              options={[
                { label: '全部狀態', value: 'all' },
                { label: '可使用', value: 'active' },
                { label: '使用完畢', value: 'used_up' },
                { label: '已過期', value: 'expired' },
                { label: '已作廢', value: 'voided' }
              ]}
              onChange={(v) =>
                setFilterDraft((d) => ({ ...d, status: v || 'all' }))
              }
            />
            <Button type='primary' onClick={applyFilters}>
              查詢
            </Button>
            <Button onClick={resetFilters}>重置</Button>
          </FilterBar>

          <DesktopTable
            rowKey='_id'
            columns={columns}
            dataSource={tickets}
            loading={loading}
            scroll={{ x: 1100 }}
            locale={{ emptyText: <Empty description='查無堂票紀錄' /> }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: false,
              onChange: (p) => setPage(p)
            }}
          />

          <MobileList>
            {loading ? (
              <Spin />
            ) : tickets.length === 0 ? (
              <Empty description='查無堂票紀錄' />
            ) : (
              tickets.map((r) => {
                const meta = STATUS_META[r.status] || {
                  text: r.status,
                  color: 'default'
                }
                return (
                  <MobileItemCard key={r._id}>
                    <MobileTopRow>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <MobileTitle>{r.templateName}</MobileTitle>
                        <MobileMeta>
                          {r.customerName || '—'}
                          {r.customerPhone ? ` · ${r.customerPhone}` : ''}
                        </MobileMeta>
                        <MobileMeta>編號 {r.ticketCode}</MobileMeta>
                      </div>
                      <Tag color={meta.color}>{meta.text}</Tag>
                    </MobileTopRow>
                    <MobileMetaRow>
                      <span>
                        剩餘<b>{r.remainingCount}</b>/{r.totalCount}
                      </span>
                      <span>
                        到期
                        <b>
                          {formatTz(r.expiresAt, 'MM/DD', '—')}
                        </b>
                      </span>
                      <span>
                        建立
                        <b>
                          {r.createdAt
                            ? formatTz(r.createdAt, 'MM/DD')
                            : '—'}
                        </b>
                      </span>
                    </MobileMetaRow>
                    <MobileActionRow>
                      <Button
                        icon={<EyeOutlined />}
                        onClick={() => openDetail(r)}
                      >
                        詳情
                      </Button>
                      <Button
                        icon={<UserOutlined />}
                        onClick={() =>
                          navigate(`/customers/${r.customerId}`)
                        }
                      >
                        會員
                      </Button>
                    </MobileActionRow>
                  </MobileItemCard>
                )
              })
            )}
          </MobileList>

          {total > pageSize && (
            <MobilePagination>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger={false}
                size='small'
                onChange={(p) => setPage(p)}
              />
            </MobilePagination>
          )}
        </TableCard>
      </ContentWrapper>

      {/* 詳情 Drawer */}
      <Drawer
        open={drawerOpen}
        title='堂票詳情'
        width={520}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
        extra={
          detail && detail.rawStatus !== 'voided' ? (
            <Space>
              <Button onClick={() => openAction('redeem')}>扣張</Button>
              <Button onClick={() => openAction('refund')}>退張</Button>
              <Button danger onClick={() => openAction('void')}>
                作廢
              </Button>
            </Space>
          ) : null
        }
      >
        {detailLoading || !detail ? (
          <Spin />
        ) : (
          <>
            <Descriptions column={1} bordered size='small'>
              <Descriptions.Item label='堂票編號'>
                <Text code>{detail.ticketCode}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='堂票名稱'>
                {detail.templateName}
              </Descriptions.Item>
              <Descriptions.Item label='會員'>
                {detail.customerName}
                {detail.customerPhone ? ` (${detail.customerPhone})` : ''}
              </Descriptions.Item>
              <Descriptions.Item label='狀態'>
                <Tag color={STATUS_META[detail.status]?.color}>
                  {STATUS_META[detail.status]?.text || detail.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label='剩餘 / 總張數'>
                <Text strong>{detail.remainingCount}</Text> /{' '}
                {detail.totalCount}
              </Descriptions.Item>
              <Descriptions.Item label='發放日期'>
                {formatTz(detail.issuedAt, 'YYYY/MM/DD HH:mm', '—')}
              </Descriptions.Item>
              <Descriptions.Item label='有效日期'>
                {formatTz(detail.expiresAt, 'YYYY/MM/DD HH:mm', '—')}
              </Descriptions.Item>
              <Descriptions.Item label='適用服務'>
                {!detail.applicableServiceIds ||
                detail.applicableServiceIds.length === 0 ? (
                  <Tag>不限</Tag>
                ) : (
                  <Space wrap size={[4, 4]}>
                    {detail.applicableServiceIds.map((id) => (
                      <Tag key={String(id)}>
                        {serviceMap.get(String(id))?.name || '已刪除服務'}
                      </Tag>
                    ))}
                  </Space>
                )}
              </Descriptions.Item>
              <Descriptions.Item label='銷售人員'>
                {detail.salesPersonnel || '—'}
              </Descriptions.Item>
              <Descriptions.Item label='支付方式'>
                {detail.paymentMethod === 'card'
                  ? `💳 刷卡${feeSettings.cardFeeRate != null ? `（手續費 ${feeSettings.cardFeeRate}%）` : ''}`
                  : detail.paymentMethod === 'mobile_payment'
                  ? `📱 行動支付${feeSettings.mobilePaymentFeeRate != null ? `（手續費 ${feeSettings.mobilePaymentFeeRate}%）` : ''}`
                  : detail.paymentMethod === 'transfer'
                  ? '🏦 轉帳'
                  : '💵 現金'}
              </Descriptions.Item>
              <Descriptions.Item label='售價'>
                {detail.price ? `$ ${Number(detail.price).toLocaleString()}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label='備註'>
                {detail.note || '—'}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 24 }}>
              使用歷程
            </Title>
            {detail.usages && detail.usages.length > 0 ? (
              <Timeline
                items={detail.usages.map((u) => ({
                  color:
                    u.action === 'redeem'
                      ? 'red'
                      : u.action === 'refund'
                        ? 'green'
                        : u.action === 'void'
                          ? 'gray'
                          : 'blue',
                  children: (
                    <div>
                      <Text strong>
                        {u.action === 'redeem'
                          ? `扣 ${u.count} 張`
                          : u.action === 'refund'
                            ? `退 ${u.count} 張`
                            : u.action === 'void'
                              ? `作廢`
                              : `發放 ${u.count} 張`}
                      </Text>
                      <Text type='secondary' style={{ marginLeft: 8 }}>
                        剩餘 {u.remainingAfter}
                      </Text>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {u.serviceName ? `${u.serviceName}・` : ''}
                        {u.action === 'redeem' && u.performerName
                          ? `施作：${u.performerName}・`
                          : ''}
                        {formatTz(u.createdAt, 'YYYY/MM/DD HH:mm')}
                      </div>
                      {u.note && (
                        <div style={{ fontSize: 12, color: '#666' }}>
                          備註：{u.note}
                        </div>
                      )}
                    </div>
                  )
                }))}
              />
            ) : (
              <Empty description='尚無使用紀錄' />
            )}
          </>
        )}
      </Drawer>

      {/* Issue modal */}
      <Modal
        open={issueOpen}
        title='發放堂票'
        okText='發放'
        cancelText='取消'
        onOk={submitIssue}
        onCancel={() => setIssueOpen(false)}
        confirmLoading={issueSubmitting}
        destroyOnClose
        width={520}
      >
        <Form form={issueForm} layout='vertical'>
          <Form.Item label='發放性質' name='issueType' initialValue='sale'>
            <Radio.Group
              optionType='button'
              buttonStyle='solid'
              options={[
                { label: '銷售（客人付費購買）', value: 'sale' },
                { label: '贈送 / 補發（免費）', value: 'gift' }
              ]}
            />
          </Form.Item>
          <Form.Item
            label='堂票範本'
            name='templateId'
            rules={[{ required: true, message: '請選擇範本' }]}
          >
            <Select
              placeholder='選擇要發放的堂票'
              showSearch
              optionFilterProp='label'
              options={templates
                .filter((t) => t.isActive !== false)
                .map((t) => ({
                  value: t._id,
                  label: `${t.name}（${t.totalCount} 張 / ${describeValidity(t, {
                    short: true
                  })}）`
                }))}
            />
          </Form.Item>
          <Form.Item
            label='選擇會員'
            name='customerId'
            rules={[{ required: true, message: '請選擇會員' }]}
          >
            <Select
              placeholder='輸入姓名或電話搜尋'
              showSearch
              filterOption={false}
              loading={customerLoading}
              onSearch={searchCustomers}
              notFoundContent={
                customerLoading ? <Spin size='small' /> : '無資料，請輸入關鍵字'
              }
              options={customerOptions}
            />
          </Form.Item>
          <Form.Item
            label='支付方式'
            name='paymentMethod'
            initialValue='cash'
            rules={[{ required: true, message: '請選擇支付方式' }]}
          >
            <Select
              options={[
                { value: 'cash', label: '💵 現金' },
                {
                  value: 'card',
                  label: feeSettings.cardFeeRate != null
                    ? `💳 刷卡（手續費 ${feeSettings.cardFeeRate}%）`
                    : '💳 刷卡（信用卡 / 簽帳卡）'
                },
                {
                  value: 'mobile_payment',
                  label: feeSettings.mobilePaymentFeeRate != null
                    ? `📱 行動支付（手續費 ${feeSettings.mobilePaymentFeeRate}%）`
                    : '📱 行動支付（Line Pay / 街口 / Apple Pay）'
                },
                { value: 'transfer', label: '🏦 轉帳 / 匯款' }
              ]}
            />
          </Form.Item>
          <Form.Item label='銷售人員' name='salesPersonnel'>
            <Select
              allowClear
              showSearch
              optionFilterProp='label'
              placeholder='選擇店家員工（選填）'
              options={employees.map((e) => ({
                value: e.name || e.username || '',
                label: `${e.name || e.username || '未命名'}${e.position ? ` · ${e.position}` : ''}`
              }))}
            />
          </Form.Item>
          <Form.Item label='備註' name='note'>
            <Input.TextArea rows={3} maxLength={300} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 購買同意書（發放前簽署） */}
      <PurchaseConsentModal
        open={consent.open}
        onClose={() => setConsent((c) => ({ ...c, open: false }))}
        businessId={businessId}
        customerId={consent.customerId}
        customerName={consent.customerName}
        target='classTicket'
        classTicketTemplateId={consent.classTicketTemplateId}
        subjectRef={consent.subjectRef}
        reminderMode={!!consent.reminderMode}
        variables={consent.variables}
        onNoForm={handleConsentNoForm}
        onSkip={handleConsentSkip}
        onSigned={handleConsentSigned}
        onSentRemote={handleConsentSentRemote}
      />

      {/* Action modal (redeem / refund / void) */}
      <Modal
        open={actionModal.open}
        title={
          actionModal.type === 'redeem'
            ? '扣張（核銷）'
            : actionModal.type === 'refund'
              ? '退張'
              : '作廢堂票'
        }
        okText='確定'
        cancelText='取消'
        okButtonProps={{
          danger: actionModal.type === 'void'
        }}
        onOk={submitAction}
        onCancel={() =>
          setActionModal({ open: false, type: null, submitting: false })
        }
        confirmLoading={actionModal.submitting}
        destroyOnClose
      >
        <Form form={actionForm} layout='vertical'>
          {actionModal.type !== 'void' && (
            <Form.Item
              label='張數'
              name='count'
              rules={[{ required: true, message: '請輸入張數' }]}
            >
              <InputNumber
                min={1}
                max={detail?.totalCount || 999}
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}
          {actionModal.type === 'redeem' &&
            detail?.applicableServiceIds &&
            detail.applicableServiceIds.length > 0 && (
              <Form.Item label='扣張對應服務' name='serviceId'>
                <Select
                  placeholder='選擇核銷的服務（範本已綁定服務時必填）'
                  options={detail.applicableServiceIds
                    .map((id) => serviceMap.get(String(id)))
                    .filter(Boolean)
                    .map((s) => ({ value: s._id, label: s.name }))}
                />
              </Form.Item>
            )}
          {actionModal.type === 'redeem' &&
            (!detail?.applicableServiceIds ||
              detail.applicableServiceIds.length === 0) && (
              <Form.Item label='對應服務（選填）' name='serviceId'>
                <Select
                  placeholder='可選'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  options={services.map((s) => ({
                    value: s._id,
                    label: s.name
                  }))}
                />
              </Form.Item>
            )}
          {actionModal.type === 'redeem' && (
            <Form.Item
              label='施作人員'
              name='performerId'
              extra='這次是誰做的 —— 核銷業績與技術抽成算給這位（每堂 = 堂票售價 ÷ 堂數）。沒選就算給操作的人。'
            >
              <ResponsiveSelect
                placeholder='選擇施作人員'
                allowClear
                showSearch
                optionFilterProp='label'
                options={employees.map((e) => ({
                  value: e._id,
                  label: e.name || e.username
                }))}
              />
            </Form.Item>
          )}
          <Form.Item
            label={actionModal.type === 'void' ? '作廢原因' : '備註'}
            name='note'
            rules={
              actionModal.type === 'void'
                ? [{ required: true, message: '請填寫作廢原因' }]
                : []
            }
          >
            <Input.TextArea rows={3} maxLength={300} />
          </Form.Item>
        </Form>
      </Modal>
    </PageWrapper>
  )
}

export default ClassTicketRecordsPage
